import { AuthenticationAssuranceLevel, ScopeType, AuditPolicy, IdempotencyPolicy, ConcurrencyPolicy, PrivacyClassification, ActorType, } from './operation.js';
import * as OID from './operation-ids.js';
const managementFeeOperation = (id, httpMethods, readOnly = false) => ({
    id,
    name: `Management fee ${id.split('.').at(-1)}`,
    labelKey: `operations.${id}.label`,
    descriptionKey: `operations.${id}.description`,
    permission: readOnly ? 'organization:accounting:read' : 'organization:accounting:manage',
    module: 'ACCOUNTING',
    requiredAal: readOnly ? AuthenticationAssuranceLevel.AAL1 : AuthenticationAssuranceLevel.AAL2,
    scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT],
    requestedScope: {
        organizationId: [{ source: 'query', field: 'organizationId' }],
        branchId: [{ source: 'query', field: 'branchId' }],
        clientAccountId: [{ source: 'query', field: 'clientAccountId' }],
    },
    forcesClientScope: true,
    makerCheckerRequired: !readOnly,
    idempotencyPolicy: readOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
    concurrencyPolicy: readOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC_VERSION,
    auditPolicy: readOnly ? AuditPolicy.READ : AuditPolicy.SENSITIVE,
    privacyClassification: PrivacyClassification.CONFIDENTIAL,
    telemetryId: id,
    allowedActorTypes: [ActorType.USER],
    httpMethods,
    isReadOnly: readOnly,
    isFinancial: !readOnly,
    supportDelegationEligible: false,
});
const performanceFeeOperation = (id, httpMethods, readOnly = false) => ({
    ...managementFeeOperation(id, httpMethods, readOnly),
    name: `Performance fee ${id.split('.').at(-1)}`,
    permission: readOnly ? 'organization:accounting:read' : 'organization:accounting:manage',
    allowedPersonas: ['ORG_ADMIN'],
});
const journalGovernanceOperation = (id, httpMethods, readOnly = false) => ({
    id,
    name: `Journal governance ${id.split('.').at(-1)}`,
    labelKey: readOnly
        ? 'operations.accounting.journal.list.label'
        : 'operations.accounting.journal.create.label',
    descriptionKey: readOnly
        ? 'operations.accounting.journal.list.description'
        : 'operations.accounting.journal.create.description',
    permission: readOnly ? 'organization:accounting:read' : 'organization:accounting:manage',
    module: 'ACCOUNTING',
    requiredAal: readOnly ? AuthenticationAssuranceLevel.AAL1 : AuthenticationAssuranceLevel.AAL2,
    scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
    requestedScope: {
        organizationId: [{ source: 'query', field: 'organizationId' }],
        branchId: [{ source: 'query', field: 'branchId' }],
    },
    forcesClientScope: false,
    makerCheckerRequired: id.endsWith('.approve'),
    idempotencyPolicy: readOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
    concurrencyPolicy: readOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC_VERSION,
    auditPolicy: readOnly ? AuditPolicy.READ : AuditPolicy.SENSITIVE,
    privacyClassification: PrivacyClassification.RESTRICTED,
    telemetryId: id,
    allowedActorTypes: [ActorType.USER],
    allowedPersonas: ['ORG_ADMIN'],
    httpMethods,
    isReadOnly: readOnly,
    isFinancial: !readOnly,
    supportDelegationEligible: false,
});
export const ACCOUNTING_OPERATIONS = {
    [OID.ACCOUNTING_JOURNAL_TEMPLATE_VIEW]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_TEMPLATE_VIEW, ['GET'], true),
    [OID.ACCOUNTING_JOURNAL_TEMPLATE_CREATE]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_TEMPLATE_CREATE, ['POST']),
    [OID.ACCOUNTING_JOURNAL_TEMPLATE_APPROVE]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_TEMPLATE_APPROVE, ['POST']),
    [OID.ACCOUNTING_JOURNAL_SCHEDULE_VIEW]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_SCHEDULE_VIEW, ['GET'], true),
    [OID.ACCOUNTING_JOURNAL_SCHEDULE_CREATE]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_SCHEDULE_CREATE, ['POST']),
    [OID.ACCOUNTING_JOURNAL_SCHEDULE_AMEND]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_SCHEDULE_AMEND, ['PATCH']),
    [OID.ACCOUNTING_JOURNAL_SCHEDULE_CANCEL]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_SCHEDULE_CANCEL, ['POST']),
    [OID.ACCOUNTING_JOURNAL_SCHEDULE_APPROVE]: journalGovernanceOperation(OID.ACCOUNTING_JOURNAL_SCHEDULE_APPROVE, ['POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_LIST]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_LIST, ['GET'], true),
    [OID.ACCOUNTING_MANAGEMENT_FEE_GET]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_GET, ['GET'], true),
    [OID.ACCOUNTING_MANAGEMENT_FEE_CALCULATE]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_CALCULATE, ['GET', 'POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_APPROVE]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_APPROVE, ['POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_WAIVE]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_WAIVE, ['POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_INVOICE]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_INVOICE, ['POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_PAY]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_PAY, [
        'POST',
    ]),
    [OID.ACCOUNTING_MANAGEMENT_FEE_CREDIT]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_CREDIT, ['POST']),
    [OID.ACCOUNTING_MANAGEMENT_FEE_REVERSE]: managementFeeOperation(OID.ACCOUNTING_MANAGEMENT_FEE_REVERSE, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_LIST]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_LIST, ['GET'], true),
    [OID.ACCOUNTING_PERFORMANCE_FEE_CALCULATE]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_CALCULATE, ['GET', 'POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_APPROVE]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_APPROVE, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_CRYSTALLIZE]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_CRYSTALLIZE, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_INVOICE]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_INVOICE, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_INVOICE_POST]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_INVOICE_POST, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_PAY]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_PAY, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_PAYMENT_POST]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_PAYMENT_POST, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_RESTATE]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_RESTATE, ['POST']),
    [OID.ACCOUNTING_PERFORMANCE_FEE_RESTATEMENT_REVERSAL_POST]: performanceFeeOperation(OID.ACCOUNTING_PERFORMANCE_FEE_RESTATEMENT_REVERSAL_POST, ['POST']),
    [OID.ACCOUNTING_CHART_CREATE_GROUP]: {
        id: OID.ACCOUNTING_CHART_CREATE_GROUP,
        name: 'Create Account Group',
        labelKey: 'operations.accounting.coa.createGroup.label',
        descriptionKey: 'operations.accounting.coa.createGroup.description',
        permission: 'accounting:manage:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.coa.createGroup.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.createGroup',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_CHART_GET_GROUPS]: {
        id: OID.ACCOUNTING_CHART_GET_GROUPS,
        name: 'List Account Groups',
        labelKey: 'operations.accounting.coa.getGroups.label',
        descriptionKey: 'operations.accounting.coa.getGroups.description',
        permission: 'accounting:view:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.getGroups',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_CHART_CREATE_ACCOUNT]: {
        id: OID.ACCOUNTING_CHART_CREATE_ACCOUNT,
        name: 'Create Ledger Account',
        labelKey: 'operations.accounting.coa.createAccount.label',
        descriptionKey: 'operations.accounting.coa.createAccount.description',
        permission: 'accounting:manage:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.coa.createAccount.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.createAccount',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_CHART_GET_ACCOUNTS]: {
        id: OID.ACCOUNTING_CHART_GET_ACCOUNTS,
        name: 'List Ledger Accounts',
        labelKey: 'operations.accounting.coa.getAccounts.label',
        descriptionKey: 'operations.accounting.coa.getAccounts.description',
        permission: 'accounting:view:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.getAccounts',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_CHART_GET_ACCOUNT]: {
        id: OID.ACCOUNTING_CHART_GET_ACCOUNT,
        name: 'Get Ledger Account',
        labelKey: 'operations.accounting.coa.getAccount.label',
        descriptionKey: 'operations.accounting.coa.getAccount.description',
        permission: 'accounting:view:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.getAccount',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_CHART_UPDATE_ACCOUNT]: {
        id: OID.ACCOUNTING_CHART_UPDATE_ACCOUNT,
        name: 'Update Ledger Account',
        labelKey: 'operations.accounting.coa.updateAccount.label',
        descriptionKey: 'operations.accounting.coa.updateAccount.description',
        permission: 'accounting:manage:coa',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.coa.updateAccount.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.coa.updateAccount',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PUT', 'PATCH'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_CHART_GET_ACCOUNT_BALANCE]: {
        id: OID.ACCOUNTING_CHART_GET_ACCOUNT_BALANCE,
        name: 'Get Account Balance',
        labelKey: 'operations.accounting.coa.getAccountBalance.label',
        descriptionKey: 'operations.accounting.coa.getAccountBalance.description',
        permission: 'accounting:view:ledger',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.coa.getAccountBalance',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_FISCAL_YEAR_CREATE]: {
        id: OID.ACCOUNTING_FISCAL_YEAR_CREATE,
        name: 'Create Fiscal Year',
        labelKey: 'operations.accounting.fiscalYear.create.label',
        descriptionKey: 'operations.accounting.fiscalYear.create.description',
        permission: 'accounting:manage:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalYear.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalYear.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_YEAR_LIST]: {
        id: OID.ACCOUNTING_FISCAL_YEAR_LIST,
        name: 'List Fiscal Years',
        labelKey: 'operations.accounting.fiscalYear.list.label',
        descriptionKey: 'operations.accounting.fiscalYear.list.description',
        permission: 'accounting:view:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalYear.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_FISCAL_YEAR_CLOSE]: {
        id: OID.ACCOUNTING_FISCAL_YEAR_CLOSE,
        name: 'Close Fiscal Year',
        labelKey: 'operations.accounting.fiscalYear.close.label',
        descriptionKey: 'operations.accounting.fiscalYear.close.description',
        permission: 'accounting:manage:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalYear.close.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalYear.close',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_YEAR_REOPEN]: {
        id: OID.ACCOUNTING_FISCAL_YEAR_REOPEN,
        name: 'Reopen Fiscal Year',
        labelKey: 'operations.accounting.fiscalYear.reopen.label',
        descriptionKey: 'operations.accounting.fiscalYear.reopen.description',
        permission: 'accounting:manage:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalYear.reopen.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalYear.reopen',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_YEAR_GET]: {
        id: OID.ACCOUNTING_FISCAL_YEAR_GET,
        name: 'Get Fiscal Year',
        labelKey: 'operations.accounting.fiscalYear.get.label',
        descriptionKey: 'operations.accounting.fiscalYear.get.description',
        permission: 'accounting:view:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalYear.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_FISCAL_PERIODS_GENERATE]: {
        id: OID.ACCOUNTING_FISCAL_PERIODS_GENERATE,
        name: 'Generate Fiscal Periods',
        labelKey: 'operations.accounting.fiscalPeriods.generate.label',
        descriptionKey: 'operations.accounting.fiscalPeriods.generate.description',
        permission: 'accounting:manage:fiscalYear',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalPeriods.generate.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalPeriods.generate',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_PERIOD_CREATE]: {
        id: OID.ACCOUNTING_FISCAL_PERIOD_CREATE,
        name: 'Create Fiscal Period',
        labelKey: 'operations.accounting.fiscalPeriod.create.label',
        descriptionKey: 'operations.accounting.fiscalPeriod.create.description',
        permission: 'accounting:manage:fiscalPeriod',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalPeriod.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalPeriod.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_PERIOD_LIST]: {
        id: OID.ACCOUNTING_FISCAL_PERIOD_LIST,
        name: 'List Fiscal Periods',
        labelKey: 'operations.accounting.fiscalPeriod.list.label',
        descriptionKey: 'operations.accounting.fiscalPeriod.list.description',
        permission: 'accounting:view:fiscalPeriod',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalPeriod.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_FISCAL_PERIOD_LOCK]: {
        id: OID.ACCOUNTING_FISCAL_PERIOD_LOCK,
        name: 'Lock Fiscal Period',
        labelKey: 'operations.accounting.fiscalPeriod.lock.label',
        descriptionKey: 'operations.accounting.fiscalPeriod.lock.description',
        permission: 'accounting:manage:fiscalPeriod',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalPeriod.lock.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalPeriod.lock',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_FISCAL_PERIOD_UNLOCK]: {
        id: OID.ACCOUNTING_FISCAL_PERIOD_UNLOCK,
        name: 'Unlock Fiscal Period',
        labelKey: 'operations.accounting.fiscalPeriod.unlock.label',
        descriptionKey: 'operations.accounting.fiscalPeriod.unlock.description',
        permission: 'accounting:manage:fiscalPeriod',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.fiscalPeriod.unlock.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.fiscalPeriod.unlock',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_JOURNAL_CREATE]: {
        id: OID.ACCOUNTING_JOURNAL_CREATE,
        name: 'Create Journal',
        labelKey: 'operations.accounting.journal.create.label',
        descriptionKey: 'operations.accounting.journal.create.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.journal.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_OPENING_CUTOVER_PREVIEW]: {
        id: OID.ACCOUNTING_OPENING_CUTOVER_PREVIEW,
        name: 'Preview Opening Cutover',
        labelKey: 'operations.accounting.journal.create.label',
        descriptionKey: 'operations.accounting.journal.create.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.openingCutover.preview',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_OPENING_CUTOVER_APPROVE]: {
        id: OID.ACCOUNTING_OPENING_CUTOVER_APPROVE,
        name: 'Approve Opening Cutover',
        labelKey: 'operations.accounting.journal.post.label',
        descriptionKey: 'operations.accounting.journal.post.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.openingCutover.approve',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_OPENING_CUTOVER_REVERSE]: {
        id: OID.ACCOUNTING_OPENING_CUTOVER_REVERSE,
        name: 'Reverse Opening Cutover',
        labelKey: 'operations.accounting.journal.post.label',
        descriptionKey: 'operations.accounting.journal.post.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.openingCutover.reverse',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_OPENING_CUTOVER_RESTATE]: {
        id: OID.ACCOUNTING_OPENING_CUTOVER_RESTATE,
        name: 'Restate Opening Cutover',
        labelKey: 'operations.accounting.journal.post.label',
        descriptionKey: 'operations.accounting.journal.post.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.openingCutover.restate',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_OPENING_CUTOVER_GET]: {
        id: OID.ACCOUNTING_OPENING_CUTOVER_GET,
        name: 'Get Opening Cutover',
        labelKey: 'operations.accounting.journal.get.label',
        descriptionKey: 'operations.accounting.journal.get.description',
        permission: 'accounting:view:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.openingCutover.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_JOURNAL_LIST]: {
        id: OID.ACCOUNTING_JOURNAL_LIST,
        name: 'List Journals',
        labelKey: 'operations.accounting.journal.list.label',
        descriptionKey: 'operations.accounting.journal.list.description',
        permission: 'accounting:view:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.journal.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_JOURNAL_GET]: {
        id: OID.ACCOUNTING_JOURNAL_GET,
        name: 'Get Journal',
        labelKey: 'operations.accounting.journal.get.label',
        descriptionKey: 'operations.accounting.journal.get.description',
        permission: 'accounting:view:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.journal.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_JOURNAL_POST]: {
        id: OID.ACCOUNTING_JOURNAL_POST,
        name: 'Post Journal',
        labelKey: 'operations.accounting.journal.post.label',
        descriptionKey: 'operations.accounting.journal.post.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.journal.post.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.journal.post',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_JOURNAL_REVERSE]: {
        id: OID.ACCOUNTING_JOURNAL_REVERSE,
        name: 'Reverse Journal',
        labelKey: 'operations.accounting.journal.reverse.label',
        descriptionKey: 'operations.accounting.journal.reverse.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.journal.reverse.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.journal.reverse',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_POSTING_RULE_CREATE]: {
        id: OID.ACCOUNTING_POSTING_RULE_CREATE,
        name: 'Create Posting Rule',
        labelKey: 'operations.accounting.postingRule.create.label',
        descriptionKey: 'operations.accounting.postingRule.create.description',
        permission: 'accounting:manage:postingRule',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.postingRule.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.postingRule.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_POSTING_RULE_LIST]: {
        id: OID.ACCOUNTING_POSTING_RULE_LIST,
        name: 'List Posting Rules',
        labelKey: 'operations.accounting.postingRule.list.label',
        descriptionKey: 'operations.accounting.postingRule.list.description',
        permission: 'accounting:view:postingRule',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.postingRule.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_POSTING_RULE_UPDATE]: {
        id: OID.ACCOUNTING_POSTING_RULE_UPDATE,
        name: 'Update Posting Rule',
        labelKey: 'operations.accounting.postingRule.update.label',
        descriptionKey: 'operations.accounting.postingRule.update.description',
        permission: 'accounting:manage:postingRule',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.postingRule.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.postingRule.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PUT', 'PATCH'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_POSTING_RULE_TEST]: {
        id: OID.ACCOUNTING_POSTING_RULE_TEST,
        name: 'Test Posting Rule',
        labelKey: 'operations.accounting.postingRule.test.label',
        descriptionKey: 'operations.accounting.postingRule.test.description',
        permission: 'accounting:manage:postingRule',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'accounting.postingRule.test',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_AUTO_POST]: {
        id: OID.ACCOUNTING_AUTO_POST,
        name: 'Auto Post from Facts',
        labelKey: 'operations.accounting.autoPost.label',
        descriptionKey: 'operations.accounting.autoPost.description',
        permission: 'accounting:manage:journal',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.autoPost.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.autoPost',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.ACCOUNTING_TRIAL_BALANCE]: {
        id: OID.ACCOUNTING_TRIAL_BALANCE,
        name: 'View Trial Balance',
        labelKey: 'operations.accounting.ledger.trialBalance.label',
        descriptionKey: 'operations.accounting.ledger.trialBalance.description',
        permission: 'accounting:view:ledger',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.ledger.trialBalance',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_GENERAL_LEDGER]: {
        id: OID.ACCOUNTING_GENERAL_LEDGER,
        name: 'View General Ledger',
        labelKey: 'operations.accounting.ledger.generalLedger.label',
        descriptionKey: 'operations.accounting.ledger.generalLedger.description',
        permission: 'accounting:view:ledger',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.ledger.generalLedger',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.ACCOUNTING_LEDGER_REBUILD_BALANCES]: {
        id: OID.ACCOUNTING_LEDGER_REBUILD_BALANCES,
        name: 'Rebuild Ledger Balances',
        labelKey: 'operations.accounting.ledger.rebuildBalances.label',
        descriptionKey: 'operations.accounting.ledger.rebuildBalances.description',
        permission: 'accounting:manage:ledger',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.accounting.ledger.rebuildBalances.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.ledger.rebuildBalances',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
};
export const RECONCILIATION_OPERATIONS = {
    [OID.RECONCILIATION_LIST]: {
        id: OID.RECONCILIATION_LIST,
        name: 'List Reconciliations',
        labelKey: 'operations.accounting.reconciliation.list.label',
        descriptionKey: 'operations.accounting.reconciliation.list.description',
        permission: 'organization:reconciliation:list',
        permissionId: 'organization:reconciliation:list',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.reconciliation.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.RECONCILIATION_GET]: {
        id: OID.RECONCILIATION_GET,
        name: 'Get Reconciliation',
        labelKey: 'operations.accounting.reconciliation.get.label',
        descriptionKey: 'operations.accounting.reconciliation.get.description',
        permission: 'organization:reconciliation:read',
        permissionId: 'organization:reconciliation:read',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.reconciliation.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.RECONCILIATION_CREATE]: {
        id: OID.RECONCILIATION_CREATE,
        name: 'Create Reconciliation',
        labelKey: 'operations.accounting.reconciliation.create.label',
        descriptionKey: 'operations.accounting.reconciliation.create.description',
        permission: 'organization:reconciliation:create',
        permissionId: 'organization:reconciliation:create',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.reconciliation.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST', 'PATCH'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.RECONCILIATION_UPDATE]: {
        id: OID.RECONCILIATION_UPDATE,
        name: 'Update Reconciliation',
        labelKey: 'operations.accounting.reconciliation.update.label',
        descriptionKey: 'operations.accounting.reconciliation.update.description',
        permission: 'reconciliation:manage',
        permissionId: 'organization:reconciliation:manage',
        module: 'ACCOUNTING',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'accounting.reconciliation.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH', 'PUT'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
};
export const IMPORT_OPERATIONS = {
    [OID.IMPORT_LIST]: {
        id: OID.IMPORT_LIST,
        name: 'List Import Jobs',
        labelKey: 'operations.import.list.label',
        descriptionKey: 'operations.import.list.description',
        permission: 'organization:imports:list',
        permissionId: 'organization:imports:list',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.IMPORT_GET]: {
        id: OID.IMPORT_GET,
        name: 'Get Import Job',
        labelKey: 'operations.import.get.label',
        descriptionKey: 'operations.import.get.description',
        permission: 'organization:imports:list',
        permissionId: 'organization:imports:list',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.IMPORT_ROWS_LIST]: {
        id: OID.IMPORT_ROWS_LIST,
        name: 'List Import Job Rows',
        labelKey: 'operations.import.rows.list.label',
        descriptionKey: 'operations.import.rows.list.description',
        permission: 'organization:imports:list',
        permissionId: 'organization:imports:list',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'import.rows.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.IMPORT_UPLOAD]: {
        id: OID.IMPORT_UPLOAD,
        name: 'Upload Import File',
        labelKey: 'operations.import.upload.label',
        descriptionKey: 'operations.import.upload.description',
        permission: 'organization:imports:upload',
        permissionId: 'organization:imports:upload',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.upload',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.IMPORT_COMMIT]: {
        id: OID.IMPORT_COMMIT,
        name: 'Commit Import Job',
        labelKey: 'operations.import.commit.label',
        descriptionKey: 'operations.import.commit.description',
        permission: 'organization:imports:commit',
        permissionId: 'organization:imports:commit',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.import.commit.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.commit',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.IMPORT_VALIDATE]: {
        id: OID.IMPORT_VALIDATE,
        name: 'Validate Import Job',
        labelKey: 'operations.import.validate.label',
        descriptionKey: 'operations.import.validate.description',
        permission: 'organization:imports:validate',
        permissionId: 'organization:imports:validate',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.validate',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.IMPORT_RECOVER]: {
        id: OID.IMPORT_RECOVER,
        name: 'Recover Import Job',
        labelKey: 'operations.import.recover.label',
        descriptionKey: 'operations.import.recover.description',
        permission: 'organization:imports:retry',
        permissionId: 'organization:imports:retry',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.import.recover.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.recover',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.IMPORT_CANCEL]: {
        id: OID.IMPORT_CANCEL,
        name: 'Cancel Import Job',
        labelKey: 'operations.import.cancel.label',
        descriptionKey: 'operations.import.cancel.description',
        permission: 'organization:imports:cancel',
        permissionId: 'organization:imports:cancel',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.import.cancel.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'import.cancel',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
};
function amlCaseOperation(id, name, permission, httpMethods, isReadOnly, makerCheckerRequired = false) {
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        permission,
        permissionId: permission,
        module: 'REGULATORY',
        requiredAal: isReadOnly ? AuthenticationAssuranceLevel.AAL1 : AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired,
        ...(makerCheckerRequired && {
            makerCheckerReasonKey: `operations.${id}.makerChecker.reason`,
        }),
        idempotencyPolicy: isReadOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: isReadOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: isReadOnly ? AuditPolicy.READ : AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER],
        httpMethods,
        isReadOnly,
        isFinancial: false,
        supportDelegationEligible: false,
    };
}
export const REGULATORY_OPERATIONS = {
    [OID.COMPLIANCE_AML_CASES_LIST]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_LIST, 'List AML Cases', 'organization:reports:list', ['GET'], true),
    [OID.COMPLIANCE_AML_CASES_GET]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_GET, 'Get AML Case', 'organization:reports:list', ['GET'], true),
    [OID.COMPLIANCE_AML_CASES_OPEN]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_OPEN, 'Open AML Case', 'organization:reports:run', ['POST'], false),
    [OID.COMPLIANCE_AML_CASES_REVIEW]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_REVIEW, 'Review AML Case', 'organization:reports:run', ['POST'], false, true),
    [OID.COMPLIANCE_AML_CASES_REPORT]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_REPORT, 'Report AML Case', 'organization:reports:run', ['POST'], false, true),
    [OID.COMPLIANCE_AML_CASES_CLOSE]: amlCaseOperation(OID.COMPLIANCE_AML_CASES_CLOSE, 'Close AML Case', 'organization:reports:run', ['POST'], false, true),
    [OID.REGULATORY_REPORT_DEFINITION_LIST]: {
        id: OID.REGULATORY_REPORT_DEFINITION_LIST,
        name: 'List Report Definitions',
        labelKey: 'operations.regulatory.reportDefinition.list.label',
        descriptionKey: 'operations.regulatory.reportDefinition.list.description',
        permission: 'organization:reports:list',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_DEFINITION_LIST,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_REPORT_DEFINITION_GET]: {
        id: OID.REGULATORY_REPORT_DEFINITION_GET,
        name: 'Get Report Definition',
        labelKey: 'operations.regulatory.reportDefinition.get.label',
        descriptionKey: 'operations.regulatory.reportDefinition.get.description',
        permission: 'organization:reports:read',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_DEFINITION_GET,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_REPORT_DEFINITION_CREATE]: {
        id: OID.REGULATORY_REPORT_DEFINITION_CREATE,
        name: 'Create Report Definition',
        labelKey: 'operations.regulatory.reportDefinition.create.label',
        descriptionKey: 'operations.regulatory.reportDefinition.create.description',
        permission: 'organization:reports:schedule',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportDefinition.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_DEFINITION_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_DEFINITION_UPDATE]: {
        id: OID.REGULATORY_REPORT_DEFINITION_UPDATE,
        name: 'Update Report Definition',
        labelKey: 'operations.regulatory.reportDefinition.update.label',
        descriptionKey: 'operations.regulatory.reportDefinition.update.description',
        permission: 'organization:reports:schedule',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportDefinition.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_DEFINITION_UPDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_DEFINITION_DISABLE]: {
        id: OID.REGULATORY_REPORT_DEFINITION_DISABLE,
        name: 'Disable Report Definition',
        labelKey: 'operations.regulatory.reportDefinition.disable.label',
        descriptionKey: 'operations.regulatory.reportDefinition.disable.description',
        permission: 'organization:reports:schedule',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportDefinition.disable.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_DEFINITION_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_GENERATE]: {
        id: OID.REGULATORY_REPORT_RUN_GENERATE,
        name: 'Generate Report Run',
        labelKey: 'operations.regulatory.reportRun.generate.label',
        descriptionKey: 'operations.regulatory.reportRun.generate.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportRun.generate.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_GENERATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_LIST]: {
        id: OID.REGULATORY_REPORT_RUN_LIST,
        name: 'List Report Runs',
        labelKey: 'operations.regulatory.reportRun.list.label',
        descriptionKey: 'operations.regulatory.reportRun.list.description',
        permission: 'organization:reports:list',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_LIST,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_REPORT_RUN_GET]: {
        id: OID.REGULATORY_REPORT_RUN_GET,
        name: 'Get Report Run',
        labelKey: 'operations.regulatory.reportRun.get.label',
        descriptionKey: 'operations.regulatory.reportRun.get.description',
        permission: 'organization:reports:read',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_GET,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_REPORT_RUN_ARTIFACT_GET]: {
        id: OID.REGULATORY_REPORT_RUN_ARTIFACT_GET,
        name: 'Get Report Run Artifact',
        labelKey: 'operations.regulatory.reportRun.artifact.get.label',
        descriptionKey: 'operations.regulatory.reportRun.artifact.get.description',
        permission: 'organization:reports:read',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_ARTIFACT_GET,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_REPORT_RUN_EXPORT]: {
        id: OID.REGULATORY_REPORT_RUN_EXPORT,
        name: 'Export Report Run',
        labelKey: 'operations.regulatory.reportRun.export.label',
        descriptionKey: 'operations.regulatory.reportRun.export.description',
        permission: 'organization:reports:export',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [{ source: 'query', field: 'clientAccountId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_EXPORT,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['POST'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_CANCEL]: {
        id: OID.REGULATORY_REPORT_RUN_CANCEL,
        name: 'Cancel Report Run',
        labelKey: 'operations.regulatory.reportRun.cancel.label',
        descriptionKey: 'operations.regulatory.reportRun.cancel.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'query', field: 'organizationId' }],
            branchId: [{ source: 'query', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.OPTIONAL,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_CANCEL,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_RETRY]: {
        id: OID.REGULATORY_REPORT_RUN_RETRY,
        name: 'Retry Report Run',
        labelKey: 'operations.regulatory.reportRun.retry.label',
        descriptionKey: 'operations.regulatory.reportRun.retry.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'query', field: 'organizationId' }],
            branchId: [{ source: 'query', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.OPTIONAL,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_RETRY,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_VALIDATE]: {
        id: OID.REGULATORY_REPORT_RUN_VALIDATE,
        name: 'Validate Report Run',
        labelKey: 'operations.regulatory.reportRun.validate.label',
        descriptionKey: 'operations.regulatory.reportRun.validate.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportRun.validate.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_VALIDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_REPORT_RUN_SUBMIT]: {
        id: OID.REGULATORY_REPORT_RUN_SUBMIT,
        name: 'Submit Report Run',
        labelKey: 'operations.regulatory.reportRun.submit.label',
        descriptionKey: 'operations.regulatory.reportRun.submit.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.reportRun.submit.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_REPORT_RUN_SUBMIT,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_SUBMISSION_LIST]: {
        id: OID.REGULATORY_SUBMISSION_LIST,
        name: 'List Report Submissions',
        labelKey: 'operations.regulatory.submission.list.label',
        descriptionKey: 'operations.regulatory.submission.list.description',
        permission: 'organization:reports:read',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: OID.REGULATORY_SUBMISSION_LIST,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_SUBMISSION_ACKNOWLEDGE]: {
        id: OID.REGULATORY_SUBMISSION_ACKNOWLEDGE,
        name: 'Acknowledge Report Submission',
        labelKey: 'operations.regulatory.submission.acknowledge.label',
        descriptionKey: 'operations.regulatory.submission.acknowledge.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.submission.acknowledge.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_SUBMISSION_ACKNOWLEDGE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.REGULATORY_TAX_REMITTANCE_LIST]: {
        id: OID.REGULATORY_TAX_REMITTANCE_LIST,
        name: 'List Tax Remittances',
        labelKey: 'operations.regulatory.taxRemittance.list.label',
        descriptionKey: 'operations.regulatory.taxRemittance.list.description',
        permission: 'organization:reports:read',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'query', field: 'organizationId' }],
            branchId: [{ source: 'query', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_TAX_REMITTANCE_LIST,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.REGULATORY_TAX_REMITTANCE_RECORD]: {
        id: OID.REGULATORY_TAX_REMITTANCE_RECORD,
        name: 'Record Tax Remittance',
        labelKey: 'operations.regulatory.taxRemittance.record.label',
        descriptionKey: 'operations.regulatory.taxRemittance.record.description',
        permission: 'organization:reports:run',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'query', field: 'organizationId' }],
            branchId: [{ source: 'query', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.regulatory.taxRemittance.record.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: OID.REGULATORY_TAX_REMITTANCE_RECORD,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.COMPLIANCE_NEPAL_GENERATE]: {
        id: OID.COMPLIANCE_NEPAL_GENERATE,
        name: 'Generate Nepal Compliance Report',
        labelKey: 'operations.compliance.nepal.generate.label',
        descriptionKey: 'operations.compliance.nepal.generate.description',
        permission: 'compliance.nepal:generate',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: 'compliance.nepal.generate',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.COMPLIANCE_NEPAL_EXPORT]: {
        id: OID.COMPLIANCE_NEPAL_EXPORT,
        name: 'Export Nepal Compliance Report',
        labelKey: 'operations.compliance.nepal.export.label',
        descriptionKey: 'operations.compliance.nepal.export.description',
        permission: 'compliance.nepal:export',
        module: 'REGULATORY',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: 'compliance.nepal.export',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: true,
    },
};
